import { Grid, Radio, Stack } from '@mui/joy';
import { Buildings, CurrencyDollar, Handshake } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SubtitleCard } from 'core/components/SubtitleCards/SubtitleCard';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Tooltip } from 'core/elements';
import { InventoryHelper } from 'core/helpers';
import { formatDate } from 'core/utils';
import { ICorporatePart } from 'corporate/types';
import { CostDisplayType } from 'parts/enums';
import { IExternalPricesResponse } from 'parts/types';
import { PricingHelper } from 'pricing/helpers';
import { Direction, Size, TooltipPosition } from 'theme/enums';

import { SellingPriceCard } from './SellingPriceCard';
import { ActiveCostType, useSellingPriceState } from './sellingPriceState';

interface IProps {
  corporatePart: Partial<ICorporatePart>;
  externalPricing?: IExternalPricesResponse | null;
  inline?: boolean;
}

interface IUseCostRadioProps {
  costType: ActiveCostType;
  activeCostType: ActiveCostType;
  onActiveCostTypeChange: (type: ActiveCostType) => void;
}

interface ISpaCostTooltipProps {
  spaName?: string;
  children: JSX.Element;
}

const UseCostRadio = ({ costType, activeCostType, onActiveCostTypeChange }: IUseCostRadioProps) => {
  const { t } = useTranslation();

  return (
    <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 0.5, pt: 1 }}>
      <Radio
        size={Size.SM}
        checked={activeCostType === costType}
        onChange={() => onActiveCostTypeChange(costType)}
        label={t('labelUseThisCost')}
      />
    </Stack>
  );
};

const cardContentSx = { width: 'fit-content', minWidth: '150px' };
const subtitleCardOptions = { contentSx: cardContentSx, cardSx: { height: '100%' } };

// Tooltip injects an auto-height wrapper between the grid item and the card, which would leave the
// card's `height: 100%` resolving against `auto` and drop it out of the row's stretch.
const spaTooltipChildrenSx = { height: '100%' };

const SpaCostTooltip = ({ spaName, children }: ISpaCostTooltipProps) => {
  if (!spaName) {
    return children;
  }

  return (
    <Tooltip title={spaName} placement={TooltipPosition.TOP} childrenSx={spaTooltipChildrenSx}>
      {children}
    </Tooltip>
  );
};

const WEIGHTS = {
  replacement: 1,
  spa: 1,
  selling: 1.25,
  inventory: 0.75,
};

export const AlternatePartCards = ({ corporatePart, externalPricing, inline }: IProps) => {
  const { t } = useTranslation();

  const spaStandardCost = useMemo(
    () => PricingHelper.deriveSpaStandardCost(externalPricing),
    [externalPricing]
  );

  const displayType = externalPricing?.externalReplacementCost
    ? CostDisplayType.REPLACEMENT
    : CostDisplayType.BASE;

  const replacementStandardCost =
    displayType === CostDisplayType.REPLACEMENT
      ? externalPricing?.externalReplacementStandardCost ?? null
      : externalPricing?.standardCost ?? null;

  const replacementCost =
    displayType === CostDisplayType.REPLACEMENT
      ? externalPricing?.externalReplacementCost ?? null
      : externalPricing?.purchaseCost ?? null;

  const {
    activeCostType,
    price,
    pricingMargin,
    showRadio,
    onActiveCostTypeChange,
    onPriceChange,
    onMarginChange,
  } = useSellingPriceState({ externalPricing, replacementStandardCost, spaStandardCost });

  const showSpaCard = !!externalPricing?.externalSpaCost;
  const showSellingPriceCard = !!externalPricing;

  const gridSizingFor = useMemo(() => {
    const totalWeight =
      WEIGHTS.replacement +
      (showSpaCard ? WEIGHTS.spa : 0) +
      (showSellingPriceCard ? WEIGHTS.selling : 0) +
      WEIGHTS.inventory;

    const colsFor = (weight: number) => (weight / totalWeight) * 12;

    return (weight: number) => ({
      xs: 12,
      sm: 12,
      md: 6,
      lg: inline ? 4 : colsFor(weight),
      xl: colsFor(weight),
    });
  }, [inline, showSpaCard, showSellingPriceCard]);

  return (
    <Grid container spacing={2} sx={{ margin: '0 -8px !important' }}>
      <Grid {...gridSizingFor(WEIGHTS.replacement)}>
        <SubtitleCard
          label={t('labelReplacementCost')}
          icon={<CurrencyDollar weight="fill" />}
          value={PricingHelper.formatPricingPerUom(replacementStandardCost, externalPricing?.uom)}
          extra={
            replacementStandardCost && replacementCost
              ? PricingHelper.formatPricingPerUom(replacementCost, externalPricing.uom)
              : ''
          }
          footer={
            showRadio ? (
              <UseCostRadio
                costType={ActiveCostType.REPLACEMENT}
                activeCostType={activeCostType}
                onActiveCostTypeChange={onActiveCostTypeChange}
              />
            ) : undefined
          }
          options={subtitleCardOptions}
        />
      </Grid>

      {showSpaCard && (
        <Grid {...gridSizingFor(WEIGHTS.spa)}>
          <SpaCostTooltip spaName={externalPricing?.externalSpaCostSourceName}>
            <SubtitleCard
              label={
                externalPricing?.externalSpaCostRegistrationNum
                  ? t('labelSpaCostWithRegNumber', {
                      regNumber: externalPricing.externalSpaCostRegistrationNum,
                    })
                  : t('labelSpaCost')
              }
              icon={<Handshake weight="fill" />}
              value={PricingHelper.formatPricingPerUom(spaStandardCost, externalPricing?.uom)}
              extra={
                externalPricing?.externalSpaCostEndDate
                  ? formatDate(externalPricing.externalSpaCostEndDate)
                  : DEFAULT_PLACEHOLDER
              }
              footer={
                showRadio ? (
                  <UseCostRadio
                    costType={ActiveCostType.SPA}
                    activeCostType={activeCostType}
                    onActiveCostTypeChange={onActiveCostTypeChange}
                  />
                ) : undefined
              }
              options={subtitleCardOptions}
            />
          </SpaCostTooltip>
        </Grid>
      )}

      {showSellingPriceCard && (
        <Grid {...gridSizingFor(WEIGHTS.selling)}>
          <SellingPriceCard
            price={price}
            pricingMargin={pricingMargin}
            onPriceChange={onPriceChange}
            onMarginChange={onMarginChange}
          />
        </Grid>
      )}

      <Grid {...gridSizingFor(WEIGHTS.inventory)}>
        <SubtitleCard
          label={t('labelInventory')}
          icon={<Buildings weight="fill" />}
          value={InventoryHelper.getInventoryAvailabilityLabel(
            corporatePart.inventory?.totalQty,
            corporatePart.inventory?.uom
          )}
          extra={
            InventoryHelper.isKnownQuantity(corporatePart.inventory?.userLocationQty)
              ? InventoryHelper.getInventoryAvailabilityLabel(
                  corporatePart.inventory.userLocationQty,
                  corporatePart.inventory?.uom
                )
              : corporatePart.inventory?.totalQty > 0
              ? DEFAULT_PLACEHOLDER
              : ''
          }
          options={subtitleCardOptions}
        />
      </Grid>
    </Grid>
  );
};
